import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const TYPE_CONFIG = {
  resultat_examen: { icon: '🔬', label: 'Résultat d\'examen', color: '#6C63FF' },
  annonce: { icon: '📢', label: 'Annonce', color: '#FF6B6B' },
  evenement: { icon: '📅', label: 'Événement', color: '#4ECDC4' },
  rdv: { icon: '🕐', label: 'Rendez-vous', color: '#45B7D1' },
  document: { icon: '📄', label: 'Document', color: '#96CEB4' },
  autre: { icon: '💬', label: 'Autre', color: '#FFEAA7' },
};

export default function ParentWall({ childId, currentUser }) {
  const [posts, setPosts] = useState([]);
  const [linkedPros, setLinkedPros] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [likes, setLikes] = useState({});
  const [loading, setLoading] = useState(true);

  // Formulaire nouvelle publication
  const [form, setForm] = useState({
    type: 'annonce',
    title: '',
    content: '',
    document_url: '',
    selectedPros: [],
  });

  useEffect(() => {
    console.log('childId reçu dans ParentWall:', childId);
    if (childId) {
      fetchPosts();
      fetchLinkedPros();
    }
  }, [childId]);

  async function fetchLinkedPros() {
    const { data } = await supabase
      .from('child_professionals')
      .select('professional_id, professional_name, profession')
      .eq('child_id', childId);
    if (data) setLinkedPros(data.map(d => ({
      id: d.professional_id || d.id,
      first_name: d.professional_name,
      last_name: '',
      specialty: d.profession,
    })));
  }

  async function fetchPosts() {
    setLoading(true);
    const { data } = await supabase
      .from('wall_posts')
      .select('*')
      .eq('child_id', childId)
      .order('created_at', { ascending: false });
    if (data) {
      setPosts(data);
      fetchLikesAndComments(data);
    }
    setLoading(false);
  }

  async function fetchLikesAndComments(postsData) {
    const ids = postsData.map(p => p.id);
    if (!ids.length) return;

    const { data: likesData } = await supabase
      .from('wall_post_likes')
      .select('post_id, professional_id')
      .in('post_id', ids);

    const { data: commentsData } = await supabase
      .from('wall_post_comments')
      .select('*, professionals(first_name, last_name)')
      .in('post_id', ids)
      .order('created_at', { ascending: true });

    // Grouper par post_id
    const likesMap = {};
    const commentsMap = {};
    ids.forEach(id => { likesMap[id] = []; commentsMap[id] = []; });
    likesData?.forEach(l => likesMap[l.post_id]?.push(l.professional_id));
    commentsData?.forEach(c => commentsMap[c.post_id]?.push(c));

    setLikes(likesMap);
    setComments(commentsMap);
  }

  async function handlePublish() {
    if (!form.title.trim()) return alert('Veuillez saisir un titre.');
    if (!form.selectedPros.length) return alert('Veuillez sélectionner au moins un professionnel.');

    const { data: post, error } = await supabase
      .from('wall_posts')
      .insert({
        child_id: childId,
        parent_id: currentUser.id,
        type: form.type,
        title: form.title,
        content: form.content,
        document_url: form.document_url || null,
      })
      .select()
      .single();

    if (error || !post) return alert('Erreur lors de la publication.');

    // Insérer la visibilité
    const visibilityRows = form.selectedPros.map(pid => ({
      post_id: post.id,
      professional_id: pid,
    }));
    await supabase.from('wall_post_visibility').insert(visibilityRows);

    setShowModal(false);
    setForm({ type: 'annonce', title: '', content: '', document_url: '', selectedPros: [] });
    fetchPosts();
  }

  function toggleProSelection(proId) {
    setForm(prev => {
      const already = prev.selectedPros.includes(proId);
      if (already) return { ...prev, selectedPros: prev.selectedPros.filter(id => id !== proId) };
      if (prev.selectedPros.length >= 10) return prev;
      return { ...prev, selectedPros: [...prev.selectedPros, proId] };
    });
  }

  function toggleComments(postId) {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  return (
    <div style={{ padding: '0 0 32px 0' }}>
      {/* En-tête wall */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
          📋 Wall de suivi
        </h2>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: 'linear-gradient(135deg, #6C63FF, #4ECDC4)',
            color: '#fff',
            border: 'none',
            borderRadius: 20,
            padding: '8px 18px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Publier
        </button>
      </div>

      {/* Liste des posts */}
      {loading ? (
        <p style={{ color: '#888', fontSize: 14 }}>Chargement…</p>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#aaa' }}>
          <div style={{ fontSize: 40 }}>📋</div>
          <p style={{ marginTop: 8 }}>Aucune publication pour l'instant</p>
        </div>
      ) : (
        posts.map(post => (
          <PostCard
            key={post.id}
            post={post}
            likes={likes[post.id] || []}
            comments={comments[post.id] || []}
            expanded={expandedComments[post.id]}
            onToggleComments={() => toggleComments(post.id)}
            formatDate={formatDate}
          />
        ))
      )}

      {/* Modale publication */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px 20px 0 0',
            padding: '24px 20px 40px', width: '100%', maxWidth: 480,
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Nouvelle publication</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Type */}
            <label style={labelStyle}>Type de publication</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setForm(f => ({ ...f, type: key }))}
                  style={{
                    padding: '6px 12px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
                    border: `2px solid ${form.type === key ? cfg.color : '#e0e0e0'}`,
                    background: form.type === key ? cfg.color + '20' : '#fff',
                    fontWeight: form.type === key ? 700 : 400,
                  }}
                >
                  {cfg.icon} {cfg.label}
                </button>
              ))}
            </div>

            {/* Titre */}
            <label style={labelStyle}>Titre *</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Ex : Bilan orthophonie mars 2026"
              style={inputStyle}
            />

            {/* Contenu */}
            <label style={labelStyle}>Contenu</label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Décrivez la publication…"
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />

            {/* URL document */}
            <label style={labelStyle}>Lien document (optionnel)</label>
            <input
              value={form.document_url}
              onChange={e => setForm(f => ({ ...f, document_url: e.target.value }))}
              placeholder="https://…"
              style={inputStyle}
            />

            {/* Sélection pros */}
            <label style={labelStyle}>Visible par ({form.selectedPros.length}/{Math.min(linkedPros.length, 10)} sélectionnés)</label>
            {linkedPros.length === 0 ? (
              <p style={{ fontSize: 13, color: '#888' }}>Aucun professionnel lié à cet enfant.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {/* Tout sélectionner */}
                <button
                  onClick={() => setForm(f => ({
                    ...f,
                    selectedPros: f.selectedPros.length === linkedPros.length
                      ? []
                      : linkedPros.map(p => p.id)
                  }))}
                  style={{ background: 'none', border: '1px solid #6C63FF', borderRadius: 8, padding: '6px 12px', color: '#6C63FF', fontSize: 13, cursor: 'pointer', alignSelf: 'flex-start' }}
                >
                  {form.selectedPros.length === linkedPros.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
                {linkedPros.slice(0, 10).map(pro => (
                  <div
                    key={pro.id}
                    onClick={() => toggleProSelection(pro.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${form.selectedPros.includes(pro.id) ? '#6C63FF' : '#e0e0e0'}`,
                      background: form.selectedPros.includes(pro.id) ? '#6C63FF10' : '#fff',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: 4,
                      background: form.selectedPros.includes(pro.id) ? '#6C63FF' : '#fff',
                      border: `2px solid ${form.selectedPros.includes(pro.id) ? '#6C63FF' : '#ccc'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {form.selectedPros.includes(pro.id) && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{pro.first_name} {pro.last_name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{pro.specialty}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handlePublish}
              style={{
                width: '100%', padding: '14px', borderRadius: 12,
                background: 'linear-gradient(135deg, #6C63FF, #4ECDC4)',
                color: '#fff', border: 'none', fontSize: 16,
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              Publier
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PostCard({ post, likes, comments, expanded, onToggleComments, formatDate }) {
  const cfg = TYPE_CONFIG[post.type] || TYPE_CONFIG.autre;

  return (
    <div style={{
      background: '#fff', borderRadius: 16, marginBottom: 16,
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden',
    }}>
      {/* Barre colorée top */}
      <div style={{ height: 4, background: cfg.color }} />

      <div style={{ padding: '16px' }}>
        {/* Type + date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{
            background: cfg.color + '20', color: cfg.color,
            borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600,
          }}>
            {cfg.icon} {cfg.label}
          </span>
          <span style={{ fontSize: 12, color: '#aaa' }}>{formatDate(post.created_at)}</span>
        </div>

        {/* Titre */}
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>{post.title}</h3>

        {/* Contenu */}
        {post.content && <p style={{ margin: '0 0 10px', fontSize: 14, color: '#555', lineHeight: 1.5 }}>{post.content}</p>}

        {/* Document */}
        {post.document_url && (
          <a href={post.document_url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6C63FF', fontWeight: 600 }}>
            📎 Voir le document
          </a>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 16, marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          <span style={{ fontSize: 13, color: '#888' }}>
            ❤️ {likes.length} like{likes.length > 1 ? 's' : ''}
          </span>
          <button
            onClick={onToggleComments}
            style={{ background: 'none', border: 'none', fontSize: 13, color: '#6C63FF', cursor: 'pointer', fontWeight: 600 }}
          >
            💬 {comments.length} commentaire{comments.length > 1 ? 's' : ''}
          </button>
        </div>

        {/* Commentaires */}
        {expanded && (
          <div style={{ marginTop: 12 }}>
            {comments.length === 0 ? (
              <p style={{ fontSize: 13, color: '#aaa' }}>Aucun commentaire pour l'instant.</p>
            ) : (
              comments.map(c => (
                <div key={c.id} style={{
                  background: '#f8f8ff', borderRadius: 10, padding: '10px 12px', marginBottom: 8,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                    {c.professionals?.first_name} {c.professionals?.last_name}
                  </div>
                  <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{c.content}</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>{formatDate(c.created_at)}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: '#555', marginBottom: 6, marginTop: 12,
};

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10,
  border: '1.5px solid #e0e0e0', fontSize: 14,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
};